import { asyncAction } from "../common"
import { getState } from "storeAccess"
import { limsRequest } from "./limsRequest"
import * as sampleView from "../sampleView"
import * as analyses from "../analyses"
import * as samples from "../samples"
import * as plannableSamples from "../plannableSamples"
import * as limsSampleQc from "./limsSampleQc"

// RERUN-link records (PRD-003 Area 4 / SPEC-010, SPEC-054). When QA signs a `may-reanalyse`
// decision — or an analyst signs a "sample lost/destroyed" discard deviation — a RERUN sample must
// come into existence: same human sample number (`sampleName`) + `productName` as the original, a
// new backend id, a fresh `uniqueSampleId`, status RERUN, UNPLANNED and ready for the planner. The
// ORIGINAL sample stays untouched on its completed run.
//
// SPEC-054: the BACKEND creates that sample. This slice used to create it here first, via
// samples.createSample, and pass the new id to /lims/reruns. That never worked: createSample
// resolves its `analysisIds` against analysis TEMPLATES, and both call sites could only supply the
// id of the analysis RUN, so it failed with "Could not find the analysis template" — silently on
// the QA path (Plannertech-user#4078), and in the signature dialog on the discard path (#4079).
// POST /lims/reruns now derives everything from the original sample and returns the link with
// `rerunSampleId` already set. See docs/specs/SPEC-054-rerun-sample-created-server-side.md.
//
// Record shape:
//   { id, originalSampleId, rerunSampleId, sampleNumber, productName, analysisId,
//     qaDecisionId, createdAt }
const initialState = { items: [], isLoading: false }

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsReruns/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsReruns/fetch":
      return { items: action.res, isLoading: false }
    case "limsReruns/create":
      return { ...state, items: [...state.items, action.res] }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsReruns/fetch", (_dispatch) =>
  limsRequest("GET", "/lims/reruns").then((res) => res.items)
)

// Create the RERUN for an original sample, GUARDED by a prior `may-reanalyse` QA decision
// existing for the original sample's OOS. `args`:
//   { originalSample: { id, sampleName, productName },
//     qaDecisionId, analysisId, hasApproval }
// `hasApproval` is the caller-resolved "a may-reanalyse decision exists" guard (the caller has
// the qaDecisions slice in scope); the thunk also rejects defensively if it is false. On success
// the SERVER has created the rerun sample (+ cloned its sample-QC) and written the audit entry;
// this thunk stores the returned link and refreshes the sample list so the new unplanned sample
// appears without a reload.
export const createRerun = asyncAction(
  "limsReruns/create",
  (dispatch, { originalSample, qaDecisionId, analysisId, hasApproval, viaDiscard = false, signature = null } = {}) => {
    if (!originalSample || !originalSample.id) {
      return Promise.reject(new Error("createRerun: no original sample"))
    }
    // SPEC-031: a rerun is authorised EITHER by a QA may-reanalyse decision (hasApproval) OR by a
    // signed "sample discarded (lost/destroyed)" deviation (viaDiscard). One of the two is required.
    if (!hasApproval && !viaDiscard) {
      return Promise.reject(new Error("createRerun: a may-reanalyse decision or a discard authorization is required"))
    }
    // Don't create a second rerun for the same original.
    if (selectRerunForOriginal(getState(), originalSample.id)) {
      return Promise.reject(new Error("createRerun: a rerun already exists for this sample"))
    }

    const sampleNumber = originalSample.sampleName || ""
    const productName = originalSample.productName || null

    // SPEC-054: no client-side sample creation. `rerunSampleId` is deliberately NOT sent — the
    // server owns the rerun sample's identity and ignores a client-supplied one (R7).
    const record = {
      originalSampleId: originalSample.id,
      sampleNumber,
      productName,
      analysisId: analysisId || null,
      qaDecisionId: qaDecisionId || null,
      // SPEC-031: a discard-authorised rerun records WHY it was raised (a lost/destroyed sample),
      // distinct from a QA may-reanalyse rerun (which carries a qaDecisionId instead).
      reason: viaDiscard ? "discard" : null,
      discardReason: viaDiscard && signature ? signature.reason || null : null,
    }
    // The backend creates the rerun sample (+ clones its sample-QC), dedups on originalSampleId and
    // writes the limsAuditRerunCreated audit entry. SPEC-031: a DISCARD-authorised rerun rides the
    // analyst's e-signature password (+ reason) at the request-body top level — the same transport as
    // every other signed LIMS transition — so the server verifies + audits the deviation.
    const signFields =
      viaDiscard && signature
        ? {
            ...(signature.password ? { password: signature.password } : {}),
            ...(signature.reason ? { reason: signature.reason } : {}),
          }
        : {}
    return limsRequest("POST", "/lims/reruns", { input: record, ...signFields }).then((stored) => {
      // The rerun sample and the failed original are created/changed SERVER-side, so nothing has
      // merged them into the client. EVERY slice that renders either row has to be refreshed here,
      // or the user has to press F5 to see their own action. In this product a change that needs a
      // manual refresh to appear is not finished.
      //
      // The list is five slices, and enumerating them is the whole job — this was fixed twice
      // before by patching whichever screen was open at the time:
      //   sampleView             — the Sample View rows
      //   analyses.unplanned     — the plan's right-hand unplanned list
      //                            (state.analyses.unplannedAnalyses, RightMenu/AnalysisTasksMenu)
      //   analyses (planned)     — the plan blocks: the ORIGINAL is now failed in place on BOTH
      //                            paths (SPEC-063), and cached_sample_count excludes failed
      //                            samples, so the block's sample count changed too
      //   samples[analysisId]    — #4145 "Does not update until refresh". THIS is the one that was
      //                            missing, and it is the screen QA is standing on: ResultEntryPanel
      //                            renders its sample cards from state.samples[analysisId].items, so
      //                            after ordering a rerun the original still showed its old status
      //                            and its old sample number until a reload. `fetchForPlanned` keeps
      //                            the existing items during the refetch (stale-while-revalidate),
      //                            so there is no blank flash. Exactly what samples.failSamples
      //                            already dispatches after the non-LIMS fail.
      //   plannableSamples[…]    — the assign-samples picker's per-analysis cache: the original has
      //                            left the planned list and the rerun has joined the unplanned one.
      //                            Invalidated rather than refetched — PlannableSamples refetches
      //                            itself on `needsRefresh` when it next mounts, and it is usually
      //                            not mounted at this moment.
      //   limsSampleQc           — the rerun carries a CLONED sample-QC row with a fresh
      //                            uniqueSampleId; without this the new sample reads as having no
      //                            lot/QC details on Sample View and in the QA worklist.
      dispatch(sampleView.fetch()).catch(() => {})
      dispatch(limsSampleQc.fetch()).catch(() => {})
      if (analysisId) {
        dispatch(samples.fetchForPlanned(analysisId)).catch(() => {})
        dispatch(plannableSamples.needsRefresh(analysisId))
      }
      if (getState().teamStatus.showAnalyses) {
        dispatch(analyses.fetchUnplanned()).catch(() => {})
        dispatch(analyses.fetchAndReplace()).catch(() => {})
      }
      return stored.item
    })
  }
)

// The rerun-link record for an original sample (or undefined if none yet) — the FORWARD link
// (original → its rerun).
export const selectRerunForOriginal = (state, originalSampleId) =>
  state.limsReruns.items.find((r) => r.originalSampleId === originalSampleId)

// The rerun-link record whose RERUN sample is `rerunSampleId` (or undefined) — the REVERSE link
// (rerun → its original), so a rerun sample can show "Rerun of <original>" and the chain is
// traceable both ways (SPEC-022 F2 / K16).
export const selectRerunOriginalFor = (state, rerunSampleId) =>
  state.limsReruns.items.find((r) => r.rerunSampleId === rerunSampleId)

export const selectReruns = (state) => state.limsReruns.items
